import { createContext, useState, useEffect, useCallback } from 'react';
import { Account } from '../models/Account';
import { jwtDecode } from 'jwt-decode';
import { fetchAccount } from '../services/AccountService';
import { getTeamTiers, getTeamTierForSteamId } from '../services/TeamTierService';
import { TeamTier } from '../models/TeamTier';
import { baseURL } from '../Constants';

// localStorage key the admin's real login token is stashed under while impersonating.
const ADMIN_TOKEN_KEY = 'powerstats_admin_token';

// Define the shape of AuthContext
interface AuthContextType {
    user: Account | null;
    token: string | null;
    setToken: React.Dispatch<React.SetStateAction<string | null>>;
    isBeta: boolean;
    authLoaded: boolean;
    refreshAccount: () => Promise<void>;
    // Admin "view as user": impersonate() swaps in a token that acts as the target (the app
    // re-derives everything from the token); stopImpersonating() restores the admin's own token.
    isImpersonating: boolean;
    impersonate: (steamId: string) => Promise<void>;
    stopImpersonating: () => void;
}

// Create context with default values
export const AuthContext = createContext<AuthContextType>({
    user: null,
    token: null,
    setToken: () => { },
    isBeta: false,
    authLoaded: false,
    refreshAccount: async () => { },
    isImpersonating: false,
    impersonate: async () => { },
    stopImpersonating: () => { },
});

interface AuthProviderProps {
    children: React.ReactNode;
}

// Helper function to decode and validate token
const getValidToken = (storedToken: string | null): string | null => {
    if (!storedToken) return null;

    try {
        const decodedToken: any = jwtDecode(storedToken);
        const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();

        if (expirationTime > currentTime) {
            // Token is still valid
            return storedToken;
        } else {
            // Token has expired
            localStorage.removeItem('token');
            return null;
        }
    } catch (error) {
        console.error('Error decoding stored token:', error);
        localStorage.removeItem('token');
        return null;
    }
};

// Helper function to handle post-login setup
const handleUserLogin = (token: string | null, setUser: React.Dispatch<React.SetStateAction<Account | null>>) => {
    if (!token) {
        setUser(null);
        return;
    }

    try {
        const decodedToken: any = jwtDecode(token);
        const expirationTime = decodedToken.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();

        if (expirationTime > currentTime) {
            setUser(decodedToken);
        } else {
            alert('Login expired, please log in again');
            setUser(null);
        }
    } catch (error) {
        console.error('Error decoding token:', error);
        setUser(null);
    }
};

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [token, setToken] = useState<string | null>(() => {
        let stored = getValidToken(localStorage.getItem('token'));
        // If the active token is gone/expired but we're mid-impersonation, fall back to the admin's
        // stashed login token so an expired impersonation never leaves the admin logged out.
        if (!stored) {
            const admin = getValidToken(localStorage.getItem(ADMIN_TOKEN_KEY));
            if (admin) {
                localStorage.removeItem(ADMIN_TOKEN_KEY);
                localStorage.setItem('token', admin);
                stored = admin;
            }
        }
        return stored;
    });

    const [user, setUser] = useState<Account | null>(null);
    const [isBeta, setIsBeta] = useState<boolean>(false);
    const [authLoaded, setAuthLoaded] = useState<boolean>(false);

    // Fetch full account data to sync beta/donator/premium status, plus any team donator tier.
    const refreshAccount = useCallback(async () => {
        if (!user?.steamID) {
            setIsBeta(false);
            return;
        }
        try {
            // A failing tier lookup must not cost the user their personal donator status, so the two
            // are settled independently.
            const [account, tiers] = await Promise.all([
                fetchAccount(user.steamID),
                getTeamTiers().catch(() => [] as TeamTier[]),
            ]);
            const teamTier = getTeamTierForSteamId(user.steamID, tiers);
            if (account) {
                setIsBeta(account.beta ?? false);
                // Sync donator/premium from DB since JWT may be stale
                setUser(prev => prev
                    ? { ...prev, donator: account.donator, premium: account.premium, teamTier }
                    : prev);
            } else {
                setUser(prev => prev ? { ...prev, teamTier } : prev);
            }
        } catch (error) {
            console.error('Error fetching account status:', error);
        }
    }, [user?.steamID]);

    // Update token in localStorage and handle user login state
    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
        } else {
            localStorage.removeItem('token');
        }
        handleUserLogin(token, setUser);
    }, [token]);

    // The checks above only look at expiry, which is years away, so a token the server stopped
    // accepting (signing secret rotated) would leave the user looking logged in while every
    // authenticated call fails. Ask the server once per token and log out on a 401 only: offline,
    // or an API that predates /Auth/session (404), keeps the session.
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        fetch(`${baseURL}/Auth/session`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                if (cancelled || res.status !== 401) return;
                localStorage.removeItem(ADMIN_TOKEN_KEY);
                setToken(null);
            })
            .catch(() => { /* network error: keep the session */ });
        return () => { cancelled = true; };
    }, [token]);

    // Fetch beta status when the user changes. authLoaded must NOT flip true until beta status is
    // actually known for a logged-in user — otherwise a fresh page load or a shared link redirects
    // non-admin beta users before their account (with the beta flag) has been fetched (admins escape
    // it because isAdmin is known instantly from the JWT). On the first render a token can be present
    // while `user` isn't decoded yet: in that case we WAIT (the [token] effect will populate user and
    // re-run this). Only a genuine anonymous visitor (no token at all) resolves immediately.
    useEffect(() => {
        if (user?.steamID) {
            refreshAccount().finally(() => setAuthLoaded(true));
        } else if (!token) {
            setIsBeta(false);
            setAuthLoaded(true);
        }
    }, [user?.steamID, refreshAccount, token]);

    // True when the current token is an impersonation token (has the impersonatedBy claim).
    let isImpersonating = false;
    if (token) {
        try { isImpersonating = !!(jwtDecode(token) as any).impersonatedBy; } catch { /* ignore */ }
    }

    // Ask the backend for a token that acts as `steamId` (admin-gated), stash the admin's own token,
    // swap it in, and hard-reload so the whole app re-initialises as the target.
    const impersonate = useCallback(async (steamId: string) => {
        const current = localStorage.getItem('token');
        if (!current) return;
        try {
            const res = await fetch(`${baseURL}/Account/impersonate/${steamId}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${current}` },
            });
            if (!res.ok) {
                alert(res.status === 401 || res.status === 403 ? 'Admin only.' : 'Could not impersonate that user.');
                return;
            }
            const data = await res.json();
            if (!localStorage.getItem(ADMIN_TOKEN_KEY)) localStorage.setItem(ADMIN_TOKEN_KEY, current);
            localStorage.setItem('token', data.token);
            window.location.href = '/';
        } catch {
            alert('Could not impersonate that user.');
        }
    }, []);

    // Restore the admin's own token and reload back to their session.
    const stopImpersonating = useCallback(() => {
        const admin = localStorage.getItem(ADMIN_TOKEN_KEY);
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        if (admin) localStorage.setItem('token', admin); else localStorage.removeItem('token');
        window.location.href = '/';
    }, []);

    return (
        <AuthContext.Provider value={{ setToken, user, token, isBeta, authLoaded, refreshAccount, isImpersonating, impersonate, stopImpersonating }}>
            {children}
        </AuthContext.Provider>
    );
};
